/**
 * Thin document action handlers (generate/view/save/print) wired by inline HTML.
 * Faithful to the monolith's per-document wrappers; each composes a build*Text
 * result with the doc viewer / download helpers.
 */

import { store } from '@/app/store';
import { byId } from '@/ui/dom';
import { showToast } from '@/ui/overlays';
import { formatDocHtml } from './format';
import { saveDocAsText, showPrinterPicker } from './doc-viewer';
import {
  buildMotionTextFull,
  buildAttyAffidavitText,
  buildJurorListText,
  buildVoirDirePacketText,
  printActiveCaseTabText
} from './reports';
import { buildVDBankText } from '@/features/vd';
import { applyCitationLinks } from '@/features/legal';

function textArea(id: string): HTMLTextAreaElement | null {
  return byId(id) as HTMLTextAreaElement | null;
}

/** Open the document viewer/print picker for a title + plain text. */
export function openPrintWindow(title: string, text: string): void {
  const slug = title
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 40);
  showPrinterPicker(formatDocHtml(title, text, false), title, text, slug);
}

// ── Motion in limine ────────────────────────────────────────────────
// The full motion dynamically assembles a Table of Authorities (rules + case
// law + statute) from the offline legal libraries, so generation is async.
export async function generateMotion(): Promise<void> {
  const area = textArea('motion-edit-area');
  showToast('Assembling authorities…');
  const text = await buildMotionTextFull();
  if (area) area.value = text;
  showToast('Motion generated');
}
export function viewMotion(): void {
  void generateMotion();
}
export async function saveMotion(): Promise<void> {
  const text = textArea('motion-edit-area')?.value || (await buildMotionTextFull());
  store.getState().motionSavedText = text;
  store.save();
  saveDocAsText('motion-in-limine', text);
}
export async function printMotion(): Promise<void> {
  const text = textArea('motion-edit-area')?.value || (await buildMotionTextFull());
  openPrintWindow('MOTION IN LIMINE', text);
}

// ── Attorney affidavit ──────────────────────────────────────────────
export function viewAttyAffidavit(): void {
  // Guide the attorney to the actual deletion date rather than silently
  // defaulting to today's date if they're producing this affidavit after
  // the fact (the field itself is left editable either way).
  const dateInput = byId<HTMLInputElement>('aff-date');
  const lastDeletion = store.getState().lastJurorDeletion;
  if (dateInput && !dateInput.value.trim() && lastDeletion) {
    dateInput.value = lastDeletion.timestamp;
  }
  const text = buildAttyAffidavitText();
  const area = textArea('atty-aff-edit-area');
  if (area) area.value = text;
  openPrintWindow('Attorney Affidavit', text);
  showToast('Affidavit generated');
}
export function saveAttyAffidavit(): void {
  const text = textArea('atty-aff-edit-area')?.value || buildAttyAffidavitText();
  store.getState().attyAffSavedText = text;
  store.save();
  saveDocAsText('attorney-affidavit', text);
}
export function printAttyAffidavit(): void {
  const text = textArea('atty-aff-edit-area')?.value || buildAttyAffidavitText();
  openPrintWindow('ATTORNEY AFFIDAVIT — JUROR DATA DELETION', text);
}

// ── Juror list ──────────────────────────────────────────────────────
export function viewPrintJurors(): void {
  const content = byId('pj-preview-content');
  if (content) content.textContent = buildJurorListText();
  const preview = byId('pj-preview');
  if (preview) {
    preview.style.display = 'block';
    preview.scrollIntoView({ behavior: 'smooth' });
  }
  showToast('Juror list generated');
}
export function printJurorList(): void {
  openPrintWindow('ALL JURORS BY JURY NUMBER', buildJurorListText());
}

// ── Voir dire packet ────────────────────────────────────────────────
export function viewVoirDirePrint(): void {
  const el = byId('vdp-preview-content');
  if (el) {
    const raw = buildVoirDirePacketText();
    el.innerHTML = raw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
    applyCitationLinks(el);
  }
  const preview = byId('vdp-preview');
  if (preview) {
    preview.style.display = 'block';
    preview.scrollIntoView({ behavior: 'smooth' });
  }
  showToast('Voir Dire packet generated');
}
export function printVoirDire(): void {
  openPrintWindow('VOIR DIRE PACKET', buildVoirDirePacketText());
}

// ── Voir dire question bank ─────────────────────────────────────────
export function printVDBank(): void {
  openPrintWindow('VOIR DIRE QUESTION BANK', buildVDBankText());
}
export function saveVDBank(): void {
  saveDocAsText('voir-dire-questions', buildVDBankText());
}

// ── Case tab printout ───────────────────────────────────────────────
export function printActiveCaseTab(): void {
  const text = printActiveCaseTabText();
  if (!text) {
    showToast('Nothing to print');
    return;
  }
  openPrintWindow('CASE SUMMARY', text);
}
